export type ScopeStatus = 'idle' | 'loading' | 'ready' | 'error'
export type ScopeMode = 'enter' | 'refresh'

export interface ScopedEntry<T> {
  status: ScopeStatus
  revalidating: boolean
  requestId: number
  data: T | null
}

export type ScopedEntries<T> = Record<number, ScopedEntry<T>>

export const createScopedEntry = <T>(): ScopedEntry<T> => ({
  status: 'idle',
  revalidating: false,
  requestId: 0,
  data: null,
})

export const beginEntry = <T>(entries: ScopedEntries<T>, id: number, mode: ScopeMode): number => {
  const entry = entries[id] ?? createScopedEntry<T>()
  entries[id] = entry

  entry.requestId += 1

  if (mode === 'refresh') {
    entry.revalidating = true
    return entry.requestId
  }

  entry.status = 'loading'
  entry.revalidating = false
  entry.data = null

  return entry.requestId
}

export const settleEntry = <T>(entries: ScopedEntries<T>, id: number, requestId: number, data: T | null): boolean => {
  const entry = entries[id]
  if (!entry || entry.requestId !== requestId) return false

  entry.status = 'ready'
  entry.revalidating = false
  entry.data = data

  return true
}

export const failEntry = <T>(entries: ScopedEntries<T>, id: number, requestId: number): boolean => {
  const entry = entries[id]
  if (!entry || entry.requestId !== requestId) return false

  const isFailedRefresh = entry.status === 'ready' && entry.revalidating
  if (!isFailedRefresh) entry.status = 'error'
  entry.revalidating = false

  return true
}

export const entryStatus = <T>(entries: ScopedEntries<T>, id: number | null): ScopeStatus =>
  id === null ? 'idle' : (entries[id]?.status ?? 'idle')

export const entryRevalidating = <T>(entries: ScopedEntries<T>, id: number | null): boolean =>
  id === null ? false : (entries[id]?.revalidating ?? false)

export const entryData = <T>(entries: ScopedEntries<T>, id: number | null): T | null =>
  id === null ? null : (entries[id]?.data ?? null)

export const entryMode = <T>(entries: ScopedEntries<T>, id: number, background: boolean): ScopeMode =>
  background && entryStatus(entries, id) === 'ready' ? 'refresh' : 'enter'
